import { Maybe, maybe, rd, RemoteData } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { createSimpleStore } from '@passionware/simple-store';
import { useMemo } from 'react';
import { MountElementProps } from 'v5/messages/mount-element';
import { ReactNode } from 'react';
import { WithServices } from 'v5/platform/ts/services';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import {
  CustomFieldAddDialog,
  CustomFieldCatalogItem,
  CustomFieldGroupCatalogItem
} from './CustomFieldAddDialog';
import { FieldDefinition } from './field-definition';

type CustomFieldAddDialogWidgetProps = MountElementProps &
  WithServices<[WithFeatureService, WithAppScopeService]> & {
    prompt: ReactNode;
    catalog: CustomFieldCatalogItem[];
    initialAssignedOrder: readonly number[];
    groupCatalog?: CustomFieldGroupCatalogItem[];
    initialAssignedGroupIds?: readonly number[];
    onCreate: (field: FieldDefinition) => Promise<void>;
    onSaveFields: (
      assignedIds: number[],
      assignedGroupIds: number[]
    ) => Promise<void>;
  };

export function CustomFieldAddDialogWidget(
  props: CustomFieldAddDialogWidgetProps
) {
  const initialFieldStore = useMemo(
    () =>
      createSimpleStore<RemoteData<Maybe<FieldDefinition>>>(
        rd.of<Maybe<FieldDefinition>>(null)
      ),
    []
  );
  const mutation = promiseState.useMutation(
    async (
      payload:
        | { type: 'create'; field: FieldDefinition }
        | {
            type: 'saveFields';
            assignedIds: number[];
            assignedGroupIds: number[];
          }
    ) => {
      if (payload.type === 'create') {
        await props.onCreate(payload.field);
        return;
      }
      await props.onSaveFields(payload.assignedIds, payload.assignedGroupIds);
    }
  );

  const handleCreate = async (field: Maybe<FieldDefinition>) => {
    await mutation.track({
      type: 'create',
      field: maybe.getOrThrow(field)
    });
    props.close();
  };

  const handleSaveFields = async (
    assignedIds: ReadonlySet<number>,
    assignedGroupIds: ReadonlySet<number>
  ) => {
    await mutation.track({
      type: 'saveFields',
      assignedIds: [...assignedIds],
      assignedGroupIds: [...assignedGroupIds]
    });
    props.close();
  };

  const featureFlags = props.services.featureService.useFeatureFlags(
    props.services.appScopeService.useResolvedScopeIdentity()
  );

  return rd.tryMap(featureFlags, flags => (
    <CustomFieldAddDialog
      open={props.open}
      features={flags}
      onSubmit={handleCreate}
      mutation={mutation.state}
      prompt={props.prompt}
      onCancel={props.close}
      resetStore={initialFieldStore}
      catalog={props.catalog}
      initialAssignedOrder={props.initialAssignedOrder}
      groupCatalog={props.groupCatalog}
      initialAssignedGroupIds={props.initialAssignedGroupIds}
      onSaveFields={handleSaveFields}
    />
  ));
}
